<template>
  <div class="tw:flex tw:flex-col tw:sm:flex-row tw:gap-2 tw:w-full">
    <ProductReportAnalyticsChartMetricBox
      v-for="item in metricList"
      :key="item.label"
      :label="item.label"
      :value="item.value"
      :type="item.type"
      :is-checked="item.isChecked"
      :is-disabled="item.isDisabled"
      :is-loading="isLoading"
      :conversion="item.conversion"
      @click="changeMetricVisibility"
    />
  </div>

  <EdroneSkeletonLoader v-if="isLoading" class="tw:w-full tw:h-[21.8rem]" />
  <ProductReportAnalyticsChartRenderer
    v-else
    :datasets="preparedChartData.datasets"
    :labels="preparedChartData.labels"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { DTODailyTrackerProductStatistic } from "vue-app/api/dto";
import ProductReportAnalyticsChartMetricBox, {
  ProductReportAnalyticsChartMetricBoxProps,
  ProductReportAnalyticsChartMetricBoxType,
} from "./ProductReportAnalyticsChartMetricBox.vue";
import dayjs from "dayjs";
import EdroneSkeletonLoader from "edrone-components/EdroneSkeletonLoader.vue";
import ProductReportAnalyticsChartRenderer from "./ProductReportAnalyticsChartRenderer.vue";
import { sendCustomEventToGA } from "vue-app/plugins/edroneGtag";

export interface ProductReportAnalyticsChartProps {
  statistics: DTODailyTrackerProductStatistic[];
  viewed: number | null;
  addedToCart: number | null;
  ordered: number | null;
  conversionAddedToCart: number | null;
  conversionOrdered: number | null;
  isLoading: boolean;
}

const translations = window._trans._products.content.drawer;

const { statistics, viewed, addedToCart, ordered, conversionAddedToCart, conversionOrdered } =
  defineProps<ProductReportAnalyticsChartProps>();

const visibleMetrics = ref<ProductReportAnalyticsChartMetricBoxType[]>(["addedToCart", "ordered"]);

const changeMetricVisibility = (arg: { value: boolean; type: ProductReportAnalyticsChartMetricBoxType }) => {
  sendCustomEventToGA("product-report-drawer-metric-visibility", {
    metric: arg.type,
    isVisible: arg.value,
  });

  visibleMetrics.value = arg.value
    ? [...visibleMetrics.value, arg.type]
    : visibleMetrics.value.filter((type) => type !== arg.type);
};

const isMetricVisible = (type: ProductReportAnalyticsChartMetricBoxType) => visibleMetrics.value.includes(type);
const isMetricDisabled = (value: number | null) => (value ? false : true);

const metricList = computed<ProductReportAnalyticsChartMetricBoxProps[]>(() => [
  {
    label: translations.viewed,
    value: viewed,
    type: "viewed",
    isChecked: isMetricVisible("viewed"),
    isDisabled: isMetricDisabled(viewed),
  },
  {
    label: translations.addedToCart,
    value: addedToCart,
    type: "addedToCart",
    conversion: conversionAddedToCart,
    isChecked: isMetricVisible("addedToCart"),
    isDisabled: isMetricDisabled(addedToCart),
  },
  {
    label: translations.ordered,
    value: ordered,
    type: "ordered",
    conversion: conversionOrdered,
    isChecked: isMetricVisible("ordered"),
    isDisabled: isMetricDisabled(ordered),
  },
]);

const preparedChartData = computed(() => {
  const labels: string[] = [];
  const addedToCartData: number[] = [];
  const ordered: number[] = [];
  const viewedData: number[] = [];

  statistics.forEach((entry) => {
    labels.push(dayjs(entry.date).format("DD.MM") ?? "");
    addedToCartData.push(entry.addedToCartCount ?? 0);
    ordered.push(entry.orderedByCustomerCount ?? 0);
    viewedData.push(entry.viewedCount ?? 0);
  });

  const datasets = [
    {
      label: translations.viewed,
      data: viewedData,
      backgroundColor: "transparent",
      borderColor: "#262626",
      fill: true,
      hidden: !isMetricVisible("viewed"),
    },
    {
      label: translations.ordered,
      data: ordered,
      backgroundColor: "#F6FEF9",
      borderColor: "#00CC52",
      fill: true,
      hidden: !isMetricVisible("ordered"),
    },
    {
      label: translations.addedToCart,
      data: addedToCartData,
      backgroundColor: "#F5F9FF",
      borderColor: "#006CFA",
      fill: true,
      hidden: !isMetricVisible("addedToCart"),
    },
  ];
  return { labels, datasets };
});
</script>
